import { Injectable, inject, signal } from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject, concatMap, filter, firstValueFrom, interval, map, share, tap } from 'rxjs';
import { debounceTime, startWith, switchMap } from 'rxjs/operators';

import { getChatDrafts } from '@lib/chat/core/store/chat.actions';

import { PrescriptionResponse } from '@lib/core/models/POC-prescription.interface';
import { ActionResponseBody } from '@lib/core/models/action.interface';
import { MemberProfile } from '@lib/core/models/member.interface';
import { FirebaseService } from '@lib/core/services/firebase.service';

import { MESSAGE_LIMIT, POLLING_INTERVAL_MESSAGES, POLLING_INTERVAL_THREADS, THREAD_LIMIT } from '../models/chat.const';
import { ChatMessage, ChatThread } from '../models/chat.interface';
import { CHAT_LIB_CONFIG } from '../tokens/chat.tokens';
import { ChatCommunicationService } from './chat-communication.service';

@Injectable({ providedIn: 'root' })
export class ChatStateService {
  private readonly firebaseService = inject(FirebaseService);
  private readonly chatService = inject(ChatCommunicationService);
  private readonly chatConfig = inject(CHAT_LIB_CONFIG);

  private readonly store = inject(Store);

  readonly threads = signal<ChatThread[]>([]);
  readonly messages = signal<ChatMessage[]>([]);
  readonly unreads = signal<Map<string, number>>(new Map());
  readonly selectedThread = signal<ChatThread | null>(null);
  readonly member = signal<MemberProfile | null>(null);
  readonly prescriptions = signal<PrescriptionResponse[]>([]);
  readonly memberComments = signal<ActionResponseBody[]>([]);
  readonly messagesLoading = signal<boolean>(false);
  readonly ownUserId = signal<string | null>(null);
  readonly searchTerm = signal<string>('');
  readonly initialThreadsLoading = signal<boolean>(true);

  private readonly scroll$ = new Subject<{ before: Date | null; after: Date | null }>();

  init() {
    this.initThreadsPolling();
    this.initUnreadPolling();
    this.initNotifications();
    this.initScrollMessagesEffect();
    this.loadInitialThreads().then(() => {
      this.store.dispatch(getChatDrafts());
      this.initialThreadsLoading.set(false);
    });
  }

  private async loadInitialThreads() {
    try {
      const result = await firstValueFrom(this.chatService.searchThreads({ pageSize: THREAD_LIMIT }));
      this.ownUserId.set(this.chatConfig.ownUserInfo.userId);
      console.log(this.ownUserId());
      this.threads.set(result.value || []);
    } catch (error) {
      console.error('Error loading initial threads:', error);
      this.threads.set([]);
    }
  }

  async loadThreadData(threadId: string) {
    if (!this.chatConfig.resolveThreadData) return;
    try {
      const threadData = await this.chatConfig.resolveThreadData(threadId);
      console.log('threadData: ', threadData);

      if (threadData.thread) {
        this.setSelectedThread(threadData.thread);

        if (threadData.memberProfileInfo !== undefined) this.setMemberData(threadData.memberProfileInfo, threadData.memberPrescriptions || [], threadData.memberComments || []);
        else this.setMemberData(null, [], []);
      }
    } catch (error) {
      console.error('Error loading thread data:', error);
    }
  }

  setSelectedThread(thread: ChatThread) {
    this.selectedThread.set(thread);
    this.loadMessages(thread);
  }

  setMemberData(member: MemberProfile | null, prescriptions: PrescriptionResponse[] = [], comments: ActionResponseBody[] = []) {
    this.member.set(member);
    this.prescriptions.set(prescriptions);
    this.memberComments.set(comments);
  }

  private setInitialThreadData() {
    this.member.set(null);
    this.messages.set([]);
    this.prescriptions.set([]);
    this.memberComments.set([]);
  }

  clearSelectedThread() {
    this.selectedThread.set(null);
    this.setInitialThreadData();
  }

  upsertThreads(threads: ChatThread[]) {
    const chatThreads = this.threads();
    const map = new Map<string, ChatThread>();
    chatThreads.forEach(thread => map.set(thread.id, thread));
    threads.forEach(thread => map.set(thread.id, thread));
    const resultThreads = [...map.values()].sort((a, b) => {
      const aIsPinned = this.isThreadPinned(a);
      const bIsPinned = this.isThreadPinned(b);
      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;

      return new Date(b.lastSentAt ?? 0).getTime() - new Date(a.lastSentAt ?? 0).getTime();
    });
    this.threads.set(resultThreads);
  }

  upsertMessages(messages: ChatMessage[]) {
    const map = new Map<string, ChatMessage>();
    this.messages().forEach(m => map.set(m.id, m));
    messages.forEach(m => map.set(m.id, m));
    const sorted = [...map.values()].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    this.messages.set(sorted);
  }

  markThreadAsRead(threadId: string) {
    const map = this.unreads();
    map.set(threadId, 0);
    this.unreads.set(new Map(map));
    this.chatService.markThreadRead(threadId).subscribe();
  }

  onSendMessage($event: string | FormData) {
    if (!this.selectedThread()) return;

    let message: string;
    let fileName: string;
    let fileLink: string;

    if (typeof $event === 'string') message = $event;
    else {
      fileName = $event.get('name') as string;
      fileLink = $event.get('link') as string;
    }

    const messagePayload: Partial<ChatMessage> = {
      content: message ?? '',
      threadId: this.selectedThread().id,
      topicId: this.selectedThread().topicIds?.[0],
      messageType: 'text',
      externalId: crypto.randomUUID(),
      media: { [fileName]: fileLink },
      senderName: this.chatConfig.ownUserInfo?.senderName || 'User',
    };

    if (typeof $event === 'string') delete messagePayload.media;

    this.chatService
      .sendMessage(messagePayload)
      .pipe(
        tap(result => this.upsertMessages([result])),
        switchMap(result => this.chatService.getThread(result.threadId))
      )
      .subscribe(thread => {
        this.upsertThreads([thread]);
        this.markThreadAsRead(thread.id);
      });
  }

  private initScrollMessagesEffect() {
    this.scroll$
      .pipe(
        filter(() => !!this.selectedThread()),
        debounceTime(100),
        switchMap(({ before, after }) => {
          const thread = this.selectedThread();
          return this.chatService.searchChatMessages(thread.id, {
            to: before ?? undefined,
            from: after ?? undefined,
            pageSize: MESSAGE_LIMIT,
          });
        })
      )
      .subscribe(res => {
        this.upsertMessages(res.value);
        this.messagesLoading.set(false);
      });
  }

  private initThreadsPolling() {
    const threadCheckerSubj = new Subject<ChatThread[]>();

    interval(POLLING_INTERVAL_THREADS)
      .pipe(
        share(),
        startWith(0),
        switchMap(() =>
          this.chatService.searchThreads({
            value: this.searchTerm(),
            pageSize: 100,
          })
        )
      )
      .subscribe(result => {
        this.initialThreadsLoading.set(false);
        threadCheckerSubj.next(result.value);
        this.upsertThreads(result.value);
      });

    threadCheckerSubj.pipe(switchMap(threads => this.chatService.getUnreadCountsByThreadIds(threads))).subscribe(result => {
      const unreadsMap = new Map<string, number>();
      Object.entries(result).forEach(([threadId, count]) => {
        unreadsMap.set(threadId, +count);
      });
      this.unreads.set(unreadsMap);
    });
  }

  private initUnreadPolling() {
    interval(POLLING_INTERVAL_MESSAGES)
      .pipe(switchMap(() => this.chatService.getUnreadCountsByThreadIds(this.threads())))
      .subscribe(res => {
        const unreadMap = new Map<string, number>();
        Object.entries(res).forEach(([id, count]) => unreadMap.set(id, Number(count)));
        this.unreads.set(unreadMap);
      });
  }

  private initNotifications() {
    this.firebaseService
      .getEvents()
      .pipe(
        map(event => {
          return event.data as { id: string; type: string; date: string; threadId: string; messageIds: string };
        }),
        filter(event => event.type.startsWith('chat.'))
      )
      .subscribe(payload => {
        switch (payload.type) {
          case 'chat.message.unread':
            if (this.selectedThread()?.id === payload.threadId) {
              const d = new Date(payload.date);
              d.setSeconds(d.getSeconds() - 1);
              this.onMessagesAfter(d);
            }
            this.chatService
              .getThread(payload.threadId)
              .pipe(
                tap(thread => this.upsertThreads([thread])),
                switchMap(() => this.chatService.getUnreadCount(payload.threadId))
              )
              .subscribe(result => {
                const unreads = this.unreads();
                unreads.set(payload.threadId, result.count);
                this.unreads.set(new Map(unreads));
              });
            break;
          case 'chat.message.read':
            console.log('chat message was read');
            break;
          case 'chat.message.received':
            this.displaySystemNotification(payload);
            break;
        }
      });
  }

  private displaySystemNotification(payload: any) {
    if (Notification.permission === 'granted') {
      const notification = new Notification(payload.notification?.title ?? 'У вас нове повідомлення', {
        body: payload.notification?.body,
        icon: 'assets/images/logo.svg',
      });
      // Play the sound
      const notificationAudio = document.getElementById('notification-sound') as HTMLAudioElement;
      notificationAudio.muted = false;
      notificationAudio.play().catch(error => console.error('Audio playback failed:', error));
      notification.onclick = () => window.focus();
    }
  }

  private loadMessages(thread: ChatThread) {
    this.messagesLoading.set(true);
    this.chatService
      .searchChatMessages(thread.id, {
        pageSize: MESSAGE_LIMIT,
      })
      .subscribe(res => {
        this.upsertMessages(res.value);
        this.messagesLoading.set(false);
      });
  }

  onMessagesBefore(timestamp: Date) {
    this.messagesLoading.set(true);
    this.scroll$.next({ before: timestamp, after: null });
  }

  onMessagesAfter(timestamp: Date) {
    this.messagesLoading.set(true);
    this.scroll$.next({ before: null, after: timestamp });
  }

  onMessagesRead() {
    const unreadsMap = this.unreads();
    unreadsMap.set(this.selectedThread().id, 0);
    this.unreads.set(new Map(unreadsMap));
    this.chatService.markThreadRead(this.selectedThread().id).subscribe();
  }

  onThreadSearch($event: string) {
    const query = {
      value: $event,
      sortBy: 'last_sent_at',
      sortDirection: 'desc',
      pageSize: THREAD_LIMIT,
    };
    this.searchTerm.set($event === '' ? undefined : $event);

    if (!$event) delete query.value;

    this.chatService.searchThreads(query).subscribe(result => {
      this.threads.set(result.value);
    });
  }

  pinThread(threadId: string) {
    this.chatService
      .assignThread(threadId)
      .pipe(concatMap(() => this.chatService.getThread(threadId)))
      .subscribe(thread => this.upsertThreads([thread]));
  }

  unpinThread(threadId: string) {
    this.chatService
      .unassignThread(threadId)
      .pipe(concatMap(() => this.chatService.getThread(threadId)))
      .subscribe(thread => this.upsertThreads([thread]));
  }

  private isThreadPinned(thread: ChatThread): boolean {
    const ownUserId = this.ownUserId();
    return ownUserId ? thread.assignees?.includes(ownUserId) || false : false;
  }
}
